import { Settings as SettingsIcon } from 'lucide-react';
import { usePlatform } from '../../lib/platform';
import { useAppSettingsStore } from '../../stores/app-settings-store';
import { useWindowStore } from '../../stores/window-store';
import type { WindowState } from '../../types/window';
import { TrafficLights } from './TrafficLights';
import { WindowsControls } from './WindowsControls';

interface WindowFrameProps {
	window: WindowState;
	onPointerDownTitleBar: (e: React.PointerEvent) => void;
	onDoubleClickTitleBar?: () => void;
	/** 'standard' = solid bg + bottom border above content (default).
	 *  'overlay' = transparent bg, absolutely positioned at top:0 over content.
	 *  Used for fullSizeContent windows so chrome + content share one row. */
	variant?: 'standard' | 'overlay';
}

export function WindowFrame({
	window: win,
	onPointerDownTitleBar,
	onDoubleClickTitleBar,
	variant = 'standard',
}: WindowFrameProps) {
	const closeWindow = useWindowStore((s) => s.closeWindow);
	const minimizeWindow = useWindowStore((s) => s.minimizeWindow);
	const maximizeWindow = useWindowStore((s) => s.maximizeWindow);
	const restoreWindow = useWindowStore((s) => s.restoreWindow);
	const openAppSettings = useAppSettingsStore((s) => s.openAppSettings);
	const platform = usePlatform();
	const isMac = platform === 'macos';
	const isOverlay = variant === 'overlay';

	const handleClose = () => closeWindow(win.id);
	const handleMinimize = () => minimizeWindow(win.id);
	const handleMaximize = () => (win.maximized ? restoreWindow(win.id) : maximizeWindow(win.id));
	const handleOpenSettings = (e: React.MouseEvent) => {
		e.stopPropagation();
		openAppSettings(win.appId, 'about');
	};

	return (
		<header
			className={`khal-window-frame k-titlebar flex shrink-0 items-center justify-between select-none ${
				isOverlay ? 'absolute inset-x-0 top-0 z-10' : 'relative'
			}`}
			style={{
				height: 'var(--khal-frame-height, 40px)',
				paddingLeft: 'var(--khal-frame-padding-x, 12px)',
				paddingRight: 'var(--khal-frame-padding-x, 12px)',
				background: isOverlay
					? 'transparent'
					: win.focused
						? 'var(--khal-window-frame-bg-focused, var(--khal-window-title-bg-focused))'
						: 'var(--khal-window-frame-bg, var(--khal-window-title-bg))',
				borderBottom: isOverlay
					? 'none'
					: `var(--khal-window-frame-border-bottom, 1px solid ${win.focused ? 'var(--khal-border-strong)' : 'var(--khal-border-default)'})`,
				backdropFilter: isOverlay ? 'none' : 'blur(40px) saturate(1.8)',
				WebkitBackdropFilter: isOverlay ? 'none' : 'blur(40px) saturate(1.8)',
			}}
			onPointerDown={onPointerDownTitleBar}
			onDoubleClick={onDoubleClickTitleBar}
		>
			{/* Left zone -- traffic lights on macOS */}
			<div className="relative z-10 flex shrink-0 items-center">
				{isMac && (
					<TrafficLights
						focused={win.focused}
						onClose={handleClose}
						onMinimize={handleMinimize}
						onMaximize={handleMaximize}
					/>
				)}
			</div>

			{/* Centered title */}
			<h2
				className="pointer-events-none absolute inset-x-0 truncate text-center font-medium"
				style={{
					fontSize: 'var(--khal-frame-font-size, 13px)',
					color: win.focused ? 'var(--khal-text-secondary)' : 'var(--khal-text-muted)',
					lineHeight: 'var(--khal-frame-height, 40px)',
					paddingLeft: 60,
					paddingRight: 60,
				}}
			>
				{win.title}
			</h2>

			{/* Right zone -- gear (per-app) + Windows controls on non-Mac.
			    In overlay mode on non-Mac we add a localized backdrop so the
			    buttons remain readable on maximized frameless content
			    (dogfood #187). */}
			<div
				className="relative z-10 flex shrink-0 items-center rounded-md"
				data-no-drag={isOverlay ? '' : undefined}
				style={{
					gap: 'var(--khal-controls-gap, 2px)',
					background:
						isOverlay && !isMac
							? 'var(--khal-window-controls-overlay-bg, rgba(0, 0, 0, 0.18))'
							: undefined,
					backdropFilter: isOverlay && !isMac ? 'blur(12px) saturate(1.4)' : undefined,
					WebkitBackdropFilter: isOverlay && !isMac ? 'blur(12px) saturate(1.4)' : undefined,
				}}
				onPointerDown={isOverlay ? (e) => e.stopPropagation() : undefined}
			>
				<button
					type="button"
					data-no-drag
					data-testid={`window-settings-${win.id}`}
					className="k-btn-icon flex h-6 w-6 items-center justify-center rounded transition-colors hover:bg-[var(--khal-controls-minimize-hover-bg,rgba(255,255,255,0.08))] focus-visible:bg-[var(--khal-controls-minimize-hover-bg,rgba(255,255,255,0.08))]"
					style={{ color: 'var(--khal-text-muted)', border: 'none' }}
					aria-label={`${win.title} settings`}
					onPointerDown={(e) => e.stopPropagation()}
					onClick={handleOpenSettings}
				>
					<SettingsIcon size={14} aria-hidden="true" />
				</button>
				{!isMac && (
					<WindowsControls
						maximized={win.maximized}
						onClose={handleClose}
						onMinimize={handleMinimize}
						onMaximize={handleMaximize}
					/>
				)}
			</div>
		</header>
	);
}
